import React from "react";
import { Link } from "react-router-dom";

/* ===================== Shared Layout ===================== */
const LegalLayout: React.FC<{
  title: string;
  updated: string;
  children: React.ReactNode;
}> = ({ title, updated, children }) => (
  <div style={styles.page}>
    {/* Header */}
    <header style={styles.header}>
      <Link to="/" style={styles.brand}>
        <div style={styles.brandLogo}>
          <img src="/logoZapee.jpeg" alt="Zetuka Tech" style={styles.brandLogoImg} />
        </div>
        <div>
          <div style={styles.brandName}>ZETUKA TECH</div>
          <div style={styles.brandSub}>Private Limited</div>
        </div>
      </Link>
      <Link to="/" style={styles.homeLink}>
        ← Back to Home
      </Link>
    </header>

    {/* Title band */}
    <div style={styles.titleBand}>
      <h1 style={styles.title}>{title}</h1>
      <p style={styles.updated}>Last updated: {updated}</p>
    </div>

    {/* Content */}
    <main style={styles.content}>{children}</main>

    {/* Footer */}
    <footer style={styles.footer}>
      <div style={styles.footerLinks}>
        <Link to="/privacy-policy" style={styles.footerLink}>
          Privacy Policy
        </Link>
        <Link to="/terms-conditions" style={styles.footerLink}>
          Terms &amp; Conditions
        </Link>
        <Link to="/refund-policy" style={styles.footerLink}>
          Refund Policy
        </Link>
      </div>
      <p style={styles.footerText}>
        © {new Date().getFullYear()} Zetuka Tech Private Limited. All rights reserved.
      </p>
    </footer>
  </div>
);

const Section: React.FC<{ heading: string; children: React.ReactNode }> = ({
  heading,
  children,
}) => (
  <section style={styles.section}>
    <h2 style={styles.heading}>{heading}</h2>
    <div style={styles.body}>{children}</div>
  </section>
);

/* ===================== Privacy Policy ===================== */
export const PrivacyPolicy: React.FC = () => (
  <LegalLayout title="Privacy Policy" updated="May 17, 2026">
    <p style={styles.intro}>
      Zetuka Tech Private Limited ("Zetuka Tech", "we", "us", or "our") respects
      your privacy and is committed to protecting the personal information you
      share with us. This Privacy Policy explains how we collect, use, and
      safeguard your data when you use our website, applications, and services.
    </p>

    <Section heading="1. Information We Collect">
      <p>We may collect the following types of information:</p>
      <ul style={styles.list}>
        <li>Personal details such as name, email address, and phone number.</li>
        <li>Account credentials and profile information.</li>
        <li>Order, delivery, and transaction details.</li>
        <li>Device information, IP address, and usage data.</li>
      </ul>
    </Section>

    <Section heading="2. How We Use Your Information">
      <ul style={styles.list}>
        <li>To provide, operate, and maintain our services.</li>
        <li>To process orders, payments, and deliveries.</li>
        <li>To communicate updates, support, and important notices.</li>
        <li>To improve our platform and prevent fraud or misuse.</li>
      </ul>
    </Section>

    <Section heading="3. Sharing of Information">
      <p>
        We do not sell your personal information. We may share data with trusted
        service providers (such as payment processors and delivery partners)
        strictly to deliver our services, or where required by law.
      </p>
    </Section>

    <Section heading="4. Data Security">
      <p>
        We implement industry-standard security measures to protect your data.
        However, no method of transmission over the internet is completely
        secure, and we cannot guarantee absolute security.
      </p>
    </Section>

    <Section heading="5. Your Rights">
      <p>
        You may request access to, correction of, or deletion of your personal
        data by contacting us. You may also opt out of marketing communications
        at any time.
      </p>
    </Section>

    <Section heading="6. Contact Us">
      <p>
        For any questions regarding this Privacy Policy, contact us at{" "}
        <a href="mailto:info@zapee.in" style={styles.link}>
          info@zapee.in
        </a>
        .
      </p>
    </Section>
  </LegalLayout>
);

/* ===================== Terms & Conditions ===================== */
export const TermsConditions: React.FC = () => (
  <LegalLayout title="Terms &amp; Conditions" updated="May 17, 2026">
    <p style={styles.intro}>
      These Terms &amp; Conditions ("Terms") govern your use of the website,
      applications, and services provided by Zetuka Tech Private Limited. By
      accessing or using our services, you agree to be bound by these Terms.
    </p>

    <Section heading="1. Acceptance of Terms">
      <p>
        By using our services, you confirm that you are at least 18 years old
        and capable of entering into a legally binding agreement.
      </p>
    </Section>

    <Section heading="2. Use of Services">
      <ul style={styles.list}>
        <li>You agree to use our services only for lawful purposes.</li>
        <li>You are responsible for maintaining the confidentiality of your account.</li>
        <li>You must not misuse, disrupt, or attempt to gain unauthorized access to our systems.</li>
      </ul>
    </Section>

    <Section heading="3. Orders and Payments">
      <p>
        All orders placed through our platform are subject to acceptance and
        availability. Prices, fees, and charges are displayed before checkout
        and may change without prior notice.
      </p>
    </Section>

    <Section heading="4. Intellectual Property">
      <p>
        All content, trademarks, logos, and software on our platform are the
        property of Zetuka Tech Private Limited and may not be copied or reused
        without written permission.
      </p>
    </Section>

    <Section heading="5. Limitation of Liability">
      <p>
        Zetuka Tech Private Limited is not liable for any indirect, incidental,
        or consequential damages arising from the use of, or inability to use,
        our services.
      </p>
    </Section>

    <Section heading="6. Termination">
      <p>
        We reserve the right to suspend or terminate your access to our services
        at any time for violation of these Terms.
      </p>
    </Section>

    <Section heading="7. Governing Law">
      <p>
        These Terms are governed by and construed in accordance with the laws of
        India. Any disputes shall be subject to the jurisdiction of the courts
        of India.
      </p>
    </Section>

    <Section heading="8. Contact Us">
      <p>
        For questions about these Terms, contact us at{" "}
        <a href="mailto:info@zapee.in" style={styles.link}>
          info@zapee.in
        </a>
        .
      </p>
    </Section>
  </LegalLayout>
);

/* ===================== Refund Policy ===================== */
export const RefundPolicy: React.FC = () => (
  <LegalLayout title="Refund Policy" updated="May 17, 2026">
    <p style={styles.intro}>
      At Zetuka Tech Private Limited, we strive to ensure customer satisfaction.
      This Refund Policy explains the conditions under which refunds are issued
      for orders placed through our platform.
    </p>

    <Section heading="1. Eligibility for Refund">
      <ul style={styles.list}>
        <li>The item received is damaged, defective, or incorrect.</li>
        <li>The order was not delivered within the committed time frame.</li>
        <li>The order was cancelled before processing or dispatch.</li>
      </ul>
    </Section>

    <Section heading="2. Non-Refundable Cases">
      <ul style={styles.list}>
        <li>Orders where delivery was successfully completed as described.</li>
        <li>Perishable goods that were delivered in acceptable condition.</li>
        <li>Requests made after the eligible refund window has closed.</li>
      </ul>
    </Section>

    <Section heading="3. Refund Request Window">
      <p>
        Refund requests must be raised within 48 hours of order delivery. Any
        request made after this period may not be eligible for a refund.
      </p>
    </Section>

    <Section heading="4. Refund Process">
      <p>
        Once a refund request is approved, the amount will be processed to the
        original payment method within 5–7 business days. Processing times may
        vary depending on your bank or payment provider.
      </p>
    </Section>

    <Section heading="5. Cancellations">
      <p>
        Orders may be cancelled before they are dispatched. Once an order is out
        for delivery, cancellation may not be possible.
      </p>
    </Section>

    <Section heading="6. Contact Us">
      <p>
        To request a refund or for any related questions, contact us at{" "}
        <a href="mailto:info@zapee.in" style={styles.link}>
          info@zapee.in
        </a>
        .
      </p>
    </Section>
  </LegalLayout>
);

/* ===================== Styles ===================== */
const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    color: "#1a2332",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 32px",
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #e2e8f0",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    textDecoration: "none",
    color: "inherit",
  },
  brandLogo: {
    width: 44,
    height: 44,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 6px 16px rgba(33,128,141,0.25)",
    flexShrink: 0,
  },
  brandLogoImg: { width: "100%", height: "100%", objectFit: "cover" },
  brandName: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1,
    color: "#1a2332",
  },
  brandSub: { fontSize: 11, color: "#64748b", letterSpacing: 1 },
  homeLink: {
    fontSize: 14,
    fontWeight: 600,
    color: "#21808D",
    textDecoration: "none",
  },
  titleBand: {
    backgroundColor: "#f8fafc",
    color: "#1a2332",
    padding: "48px 32px",
    textAlign: "center",
    borderBottom: "1px solid #e2e8f0",
  },
  title: { fontSize: 30, fontWeight: 700, margin: "0 0 8px 0" },
  updated: { fontSize: 14, color: "#64748b", margin: 0 },
  content: {
    maxWidth: 820,
    margin: "0 auto",
    padding: "56px 32px",
  },
  intro: {
    fontSize: 16,
    lineHeight: 1.7,
    color: "#475569",
    marginBottom: 36,
  },
  section: { marginBottom: 32 },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    margin: "0 0 12px 0",
    color: "#1a2332",
  },
  body: { fontSize: 15, lineHeight: 1.7, color: "#475569" },
  list: { paddingLeft: 22, margin: 0, display: "grid", gap: 6 },
  link: { color: "#21808D", fontWeight: 600 },
  footer: {
    backgroundColor: "#1a2332",
    color: "#fff",
    textAlign: "center",
    padding: "36px 32px",
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: 28,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  footerLink: {
    fontSize: 13,
    color: "rgba(255,255,255,0.85)",
    textDecoration: "none",
    fontWeight: 600,
  },
  footerText: { fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0 },
};
